import React, { useState, useEffect } from 'react';
import { Github, Twitter, BookOpen, Linkedin, ExternalLink, Code, Video } from 'lucide-react';

// Import images
import imgProfilePic from './images/profile-pic.jpg';
import imgDistRL from './images/distrl.png';
import imgDRO from './images/dro.png';
import imgRisk from './images/black_swan.png';
import imgMORL from './images/morl.png';
import imgQueryOpt from './images/query_opt.png';
import imgTTC from './images/ttc.png';

import './PortfolioWebsite.css';

// Table of Contents Component
const TableOfContents = ({ themes, activeTheme, setActiveTheme }) => {
  const scrollToTheme = (index) => {
    const element = document.getElementById(`theme-${index}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveTheme(index);
    }
  };

  return (
    <nav className="sticky top-4 bg-white p-4 rounded shadow mb-8">
      <h2 className="text-lg font-bold mb-4">Research Themes</h2>
      <ul className="space-y-1">
        {themes.map((theme, index) => (
          <li key={index}>
            <button
              onClick={() => scrollToTheme(index)}
              className={`text-left w-full py-2 px-3 rounded transition-colors duration-300 ${
                activeTheme === index ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'
              }`}
            >
              {theme.name}
            </button>
            {index < themes.length - 1 && (
              <div className="h-px bg-gray-200 my-1"></div>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};

const PortfolioWebsite = () => {
  const [activeTheme, setActiveTheme] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const themes = [
    {
      name: 'Value Functions for LLMs',
      image: imgTTC,
      papers: [
        {
          title: 'Value-Guided Search for Efficient Chain-of-Thought Reasoning',
          authors: '*Kaiwen Wang, *Jin Peng Zhou, *Jonathan Chang, Zhaolin Gao, Nathan Kallus, Kianté Brantley, Wen Sun',
          conference: 'Conference on Neural Information Processing Systems (NeurIPS), 2025',
          link: 'https://arxiv.org/abs/2505.17373',
          codeLink: 'https://github.com/kaiwenw/value-guided-search',
          description: 'Block-wise value-guided search for scaling test-time compute of reasoning models (DeepSeek-R1 series).'
        },
        {
          title: 'Q#: Provably Optimal Distributional RL for LLM Post-Training',
          authors: '*Jin Peng Zhou, *Kaiwen Wang, Jonathan Chang, Zhaolin Gao, Nathan Kallus, Kilian Q. Weinberger, Kianté Brantley, Wen Sun',
          conference: 'Conference on Neural Information Processing Systems (NeurIPS), 2025',
          link: 'https://arxiv.org/abs/2502.20548',
          codeLink: 'https://github.com/jinpz/q_sharp',
          description: 'Provably optimal distributional RL method for KL-regularized RL, which we apply to post-train language models.'
        }
      ]
    },
    {
      name: 'Steerable RL',
      image: imgMORL,
      papers: [
        {
          title: 'Conditional Language Policy: A General Framework for Steerable Multi-Objective Finetuning',
          authors: '*Kaiwen Wang, Rahul Kidambi, Ryan Sullivan, Alekh Agarwal, Christoph Dann, Andrea Michi, Marco Gelmi, Yunxuan Li, Raghav Gupta, Avinava Dubey, Alexandre Ramé, Johan Ferret, Geoffrey Cideron, Le Hou, Hongkun Yu, Amr Ahmed, Aranyak Mehta, Léonard Hussenot, Olivier Bachem, Edouard Leurent',
          conference: 'Findings of Empirical Methods in Natural Language Processing (EMNLP), 2024',
          link: 'https://arxiv.org/abs/2407.15762',
          description: 'Framework via multi-objective RL for effectively steering language models, achieving state-of-the-art in both steerability and Pareto-dominance.'
        },
        {
          title: 'Provable Benefits of Representational Transfer in Reinforcement Learning',
          authors: 'Alekh Agarwal, Yuda Song, Wen Sun, *Kaiwen Wang, Mengdi Wang, Xuezhou Zhang',
          conference: 'Conference on Learning Theory (COLT), 2023',
          link: 'https://arxiv.org/abs/2205.14571',
          codeLink: 'https://github.com/yudasong/briee/tree/reptransfer',
          description: 'Theoretical foundations for transferring representations in low-rank MDPs.'
        }
      ]
    },
    {
      name: 'Sample-Efficient RL via Loss Design',
      image: imgDistRL,
      papers: [
        {
          title: 'The Central Role of the Loss Function in Reinforcement Learning',
          authors: '*Kaiwen Wang, Nathan Kallus, Wen Sun',
          conference: 'Statistical Science, 2025',
          link: 'https://arxiv.org/abs/2409.12799',
          description: 'Survey paper on how changing the loss function in RL algorithms leads to adaptive sample efficiency and faster convergence.'
        },
        {
          title: 'More Benefits of Being Distributional: Second-Order Bounds for Reinforcement Learning',
          authors: '*Kaiwen Wang, Owen Oertell, Alekh Agarwal, Nathan Kallus, Wen Sun',
          conference: 'International Conference on Machine Learning (ICML), 2024',
          link: 'https://arxiv.org/abs/2402.07198',
          codeLink: 'https://github.com/Cornell-RL/DistUCB',
          description: 'Improved variance-dependent, second-order bounds for distributional RL.'
        },
        {
          title: 'The Benefits of Being Distributional: Small-Loss Bounds for Reinforcement Learning',
          authors: '*Kaiwen Wang, Kevin Zhou, Runzhe Wu, Nathan Kallus, Wen Sun',
          conference: 'Conference on Neural Information Processing Systems (NeurIPS), 2023',
          link: 'https://arxiv.org/abs/2305.15703',
          codeLink: 'https://github.com/kevinzhou497/distcb',
          description: 'First mathematical explanation of why (and when) distributional RL is better than non-distributional methods.'
        },
        // {
        //   title: 'Switching the Loss Reduces the Cost in Batch Reinforcement Learning',
        //   authors: 'Alex Ayoub, Kaiwen Wang, Vincent Liu, Samuel Robertson, James McInerney, Dawen Liang, Nathan Kallus, Csaba Szepesvári',
        //   conference: 'International Conference on Machine Learning (ICML), 2024',
        //   link: 'https://arxiv.org/abs/2403.05385',
        //   description: 'FQI with binary cross-entropy loss achieves first-order bounds.'
        // }
      ]
    },
    {
      name: 'Risk-Sensitive RL',
      image: imgRisk,
      papers: [
        {
          title: 'A Reductions Approach to Risk-Sensitive Reinforcement Learning with Optimized Certainty Equivalents',
          authors: '*Kaiwen Wang, Dawen Liang, Nathan Kallus, Wen Sun',
          conference: 'International Conference on Machine Learning (ICML), 2025',
          link: 'https://arxiv.org/abs/2403.06323',
          description: 'General framework for solving risk-sensitive RL by reducing to standard RL.'
        },
        {
          title: 'Near-Minimax-Optimal Risk-Sensitive Reinforcement Learning with CVaR',
          authors: '*Kaiwen Wang, Nathan Kallus, Wen Sun',
          conference: 'International Conference on Machine Learning (ICML), 2023',
          link: 'https://arxiv.org/abs/2302.03201',
          description: 'First minimax-optimal algorithm for risk-sensitive RL.',
        },
      ]
    },
    {
      name: 'Adversarially Robust RL',
      image: imgDRO,
      papers: [
        {
          title: 'Efficient and Sharp Off-Policy Evaluation in Robust Markov Decision Processes',
          authors: 'Andrew Bennett, Nathan Kallus, Miruna Oprescu, Wen Sun, *Kaiwen Wang',
          conference: 'Conference on Neural Information Processing Systems (NeurIPS), 2024',
          link: 'https://arxiv.org/abs/2404.00099',
          codeLink: 'https://github.com/CausalML/adversarial-ope',
          description: 'Efficient and sharp estimators for off-policy evaluation in environments with unobserved confounding or distribution shifts.'
        },
        {
          title: 'Doubly Robust Distributionally Robust Off-Policy Evaluation and Learning',
          authors: 'Nathan Kallus, Xiaojie Mao, *Kaiwen Wang, Zhengyuan Zhou',
          conference: 'International Conference on Machine Learning (ICML), 2022',
          link: 'https://arxiv.org/abs/2202.09667',
          codeLink: 'https://github.com/CausalML/doubly-robust-dropel',
          description: 'Efficient algorithms for distributionally robust contextual bandits.'
        }
      ]
    },
    {
      name: 'RL for Systems',
      image: imgQueryOpt,
      papers: [
        {
          title: 'JoinGym: An Efficient Query Optimization Environment for Reinforcement Learning',
          authors: '*Kaiwen Wang, *Junxiong Wang, Yueying Li, Nathan Kallus, Immanuel Trummer, Wen Sun',
          conference: 'Reinforcement Learning Conference (RLC), 2024',
          link: 'https://rlj.cs.umass.edu/2024/papers/Paper14.html',
          codeLink: 'https://github.com/kaiwenw/JoinGym',
          description: 'Lightweight RL environment for solving join order selection problem for database query optimization.',
        },
      ]
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      const newActiveTheme = themes.findIndex((theme, index) => {
        const element = document.getElementById(`theme-${index}`);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          return scrollPosition > offsetTop && scrollPosition < offsetTop + offsetHeight;
        }
        return false;
      });

      if (newActiveTheme !== -1 && newActiveTheme !== activeTheme) {
        setActiveTheme(newActiveTheme);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once to set initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeTheme, themes]);

  const renderAuthors = (authors) => {
    return authors.split(', ').map((author, index, array) => (
      <React.Fragment key={index}>
        {author.startsWith('*') ? (
          <strong>{author.slice(1)}</strong>
        ) : (
          author
        )}
        {index < array.length - 1 ? ', ' : ''}
      </React.Fragment>
    ));
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 font-sans">
      <header className="mb-8 sm:mb-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center">
          <div className="flex-1 w-full sm:w-3/4 pr-0 sm:pr-8">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-4">Kaiwen Wang</h1>
            <p className="text-base sm:text-lg mb-4 about-paragraph">
                Hi! I'm a researcher at <a href="https://openai.com/">OpenAI</a> working on <a href="https://en.wikipedia.org/wiki/AI_safety">AI Safety</a> and RL.
                I earned my Ph.D. from <a href="https://tech.cornell.edu/">Cornell Tech</a>, where I researched and taught RL, causal inference, and LLMs.
                During grad school, I worked at Google, Microsoft, and Netflix on projects spanning core RL theory to scalable LLM algorithms.
                Before grad school, I spent two years at Facebook building <a href="https://github.com/facebookresearch/ReAgent">the RL Platform</a>. I studied computer science at <a href="https://www.cs.cmu.edu/">Carnegie Mellon University</a>.
            </p>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-0">
              <a href="https://scholar.google.com/citations?user=HsMheBUAAAAJ&sortby=pubdate" className="flex items-center text-blue-600 hover:text-blue-800">
                <BookOpen size={24} className="mr-2" />
                <span>Google Scholar</span>
              </a>
              <a href="https://www.linkedin.com/in/kaiwenw/" className="flex items-center text-blue-600 hover:text-blue-800">
                <Linkedin size={24} className="mr-2" />
                <span>LinkedIn</span>
              </a>
              <a href="https://github.com/kaiwenw" className="flex items-center text-blue-600 hover:text-blue-800">
                <Github size={24} className="mr-2" />
                <span>GitHub</span>
              </a>
              <a href="https://twitter.com/kaiwenw_ai" className="flex items-center text-blue-600 hover:text-blue-800">
                <Twitter size={24} className="mr-2" />
                <span>Twitter</span>
              </a>
            </div>
          </div>
          <div className="w-full sm:w-1/4 flex justify-center sm:justify-end mt-4 sm:mt-0">
            <img src={imgProfilePic} alt="Kaiwen Wang" className="w-40 h-40 sm:w-48 sm:h-48 rounded-full object-cover" />
          </div>
        </div>
      </header>

      <div className="flex flex-col md:flex-row gap-8">
        <aside className="md:w-1/4 md:sticky md:top-4 md:self-start">
          <TableOfContents themes={themes} activeTheme={activeTheme} setActiveTheme={setActiveTheme} />
        </aside>

        <main className="md:w-3/4">
          {themes.map((theme, index) => (
            <section
            key={index}
            id={`theme-${index}`}
            className={`mb-12 p-4 rounded transition-colors duration-300 ${activeTheme === index ? 'bg-blue-50' : ''}`}
          >
            <h3 className="text-xl sm:text-2xl font-bold mb-4 pb-2 border-b-2 border-blue-200">{theme.name}</h3>
            <div className="flex justify-center mb-4 h-64 gap-4">
              {Array.isArray(theme.image) ? (
                <>
                  <div className="w-1/2 h-full flex items-center justify-center bg-gray-100">
                    <img
                      src={theme.image[0]}
                      alt={theme.name}
                      className="h-full w-auto max-w-full object-contain"
                    />
                  </div>
                  <div className="w-1/2 h-full flex items-center justify-center bg-gray-100">
                    <img
                      src={theme.image[1]}
                      alt={theme.name}
                      className="h-full w-auto max-w-full object-contain"
                    />
                  </div>
                </>
              ) : (
                <div className="h-full flex items-center justify-center bg-gray-100">
                  <img
                    src={theme.image}
                    alt={theme.name}
                    className="h-full w-auto max-w-full object-contain"
                  />
                </div>
              )}
            </div>

              {theme.papers.map((paper, paperIndex) => (
                <div key={paperIndex} className="mb-6 bg-white p-4 rounded shadow">
                  <h4 className="text-base sm:text-lg font-medium">{paper.title}</h4>
                  <p className="text-sm text-gray-600 mb-1">{renderAuthors(paper.authors)}</p>
                  <p className="text-sm text-gray-600 mb-2">{paper.conference}</p>
                  <p className="text-sm sm:text-base mb-2">{paper.description}</p>
                  <div className="flex flex-wrap items-center">
                    <a href={paper.link} className="text-blue-600 hover:text-blue-800 inline-flex items-center">
                      Paper <ExternalLink size={16} className="ml-1" />
                    </a>
                    {(paper.codeLink || paper.videoLink) && (
                      <span className="text-gray-400 mx-2">/</span>
                    )}
                    {paper.codeLink && (
                      <a href={paper.codeLink} className="text-green-600 hover:text-green-800 inline-flex items-center">
                        Code <Code size={16} className="ml-1" />
                      </a>
                    )}
                    {paper.codeLink && paper.videoLink && (
                      <span className="text-gray-400 mx-2">/</span>
                    )}
                    {paper.videoLink && (
                      <a href={paper.videoLink} className="text-red-600 hover:text-red-800 inline-flex items-center">
                        Video <Video size={16} className="ml-1" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
};

export default PortfolioWebsite;
